import React, { useState, useEffect } from 'react';
import { useLoading } from '../contexts/LoadingContext';

const SplashScreen = ({ onLoadingComplete }) => {
  const { progress, isInitialLoading } = useLoading();
  const [isExiting, setIsExiting] = useState(false);

  useEffect(() => {
    // When loading is complete (progress = 100%), start exit animation
    if (!isInitialLoading && progress >= 100) {
      setTimeout(() => {
        setIsExiting(true);
        setTimeout(() => {
          onLoadingComplete();
        }, 600);
      }, 300);
    }
  }, [isInitialLoading, progress, onLoadingComplete]);

  return (
    <div
      className={`fixed inset-0 z-[9999] flex items-center justify-center bg-gradient-to-br from-[#0052CC] via-[#0043A8] to-[#003380] transition-opacity duration-500 ${isExiting ? 'opacity-0' : 'opacity-100'
        }`}
    >
      {/* Animated Background Shapes - Parallax Effect */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-20 left-10 w-64 h-64 bg-white/5 rounded-full blur-3xl animate-float-slow" />
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-[#FF9900]/10 rounded-full blur-3xl animate-float-slower" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-white/3 rounded-full blur-3xl animate-pulse-slow" />
      </div>

      {/* Content */}
      <div className="relative z-10 flex flex-col items-center gap-8 px-6">
        {/* Logo */}
        <div className="text-center animate-fade-in">
          {/* Logo Image */}
          <div className="mb-4 flex justify-center">
            <img
              src="https://kontentutorialmpstore-cdn.maduraprime.com/85e24262-ce3a-4e64-8c4d-82f74f0f3afe.png"
              alt="GiziAI Logo"
              className="h-32 w-auto object-contain"
            />
          </div>
          <h1 className="text-6xl font-bold text-white mb-2 tracking-tight">
            Gizi<span className="text-[#FF9900]">AI</span>
          </h1>
          <p className="text-white/80 text-lg font-light">Marketplace Untuk MBG Sehat</p>
        </div>

        {/* Progress Bar */}
        <div className="w-80 animate-fade-in-delay">
          {/* Progress Bar Container */}
          <div className="relative h-2 bg-white/20 rounded-full overflow-hidden backdrop-blur-sm">
            {/* Progress Fill */}
            <div
              className="absolute inset-y-0 left-0 bg-gradient-to-r from-[#FF9900] to-[#FFB84D] rounded-full transition-all duration-300 ease-out"
              style={{ width: `${progress}%` }}
            >
              {/* Shimmer Effect */}
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent animate-shimmer" />
            </div>
          </div>

          {/* Percentage */}
          <div className="mt-4 text-center">
            <span className="text-white text-2xl font-bold tabular-nums">
              {Math.round(progress)}%
            </span>
            <p className="text-white/60 text-sm mt-1">Memuat aplikasi...</p>
          </div>
        </div>

        {/* Loading Dots */}
        <div className="flex gap-2 animate-fade-in-delay-2">
          <div className="w-2 h-2 bg-white/60 rounded-full animate-bounce-1" />
          <div className="w-2 h-2 bg-white/60 rounded-full animate-bounce-2" />
          <div className="w-2 h-2 bg-white/60 rounded-full animate-bounce-3" />
        </div>
      </div>

      {/* Custom Animations */}
      <style dangerouslySetInnerHTML={{ __html: `
        @keyframes float-slow {
          0%, 100% { transform: translate(0, 0) scale(1); }
          50% { transform: translate(30px, -30px) scale(1.1); }
        }
        @keyframes float-slower {
          0%, 100% { transform: translate(0, 0) scale(1); }
          50% { transform: translate(-40px, 40px) scale(1.15); }
        }
        @keyframes pulse-slow {
          0%, 100% { opacity: 0.3; transform: translate(-50%, -50%) scale(1); }
          50% { opacity: 0.5; transform: translate(-50%, -50%) scale(1.05); }
        }
        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
        @keyframes fade-in {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes bounce-1 {
          0%, 80%, 100% { transform: translateY(0); }
          40% { transform: translateY(-10px); }
        }
        @keyframes bounce-2 {
          0%, 80%, 100% { transform: translateY(0); }
          40% { transform: translateY(-10px); }
        }
        @keyframes bounce-3 {
          0%, 80%, 100% { transform: translateY(0); }
          40% { transform: translateY(-10px); }
        }

        .animate-float-slow {
          animation: float-slow 8s ease-in-out infinite;
        }
        .animate-float-slower {
          animation: float-slower 10s ease-in-out infinite;
        }
        .animate-pulse-slow {
          animation: pulse-slow 6s ease-in-out infinite;
        }
        .animate-shimmer {
          animation: shimmer 2s infinite;
        }
        .animate-fade-in {
          animation: fade-in 0.8s ease-out;
        }
        .animate-fade-in-delay {
          animation: fade-in 0.8s ease-out 0.2s both;
        }
        .animate-fade-in-delay-2 {
          animation: fade-in 0.8s ease-out 0.4s both;
        }
        .animate-bounce-1 {
          animation: bounce-1 1.4s ease-in-out infinite;
        }
        .animate-bounce-2 {
          animation: bounce-2 1.4s ease-in-out 0.2s infinite;
        }
        .animate-bounce-3 {
          animation: bounce-3 1.4s ease-in-out 0.4s infinite;
        }
      ` }} />
    </div>
  );
};

export default SplashScreen;
